import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  auth,
  createUserWithEmailAndPassword,
  facebookAuthProvider,
  githubAuthProvider,
  googleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  twitterAuthProvider,
  updateProfile,
} from './firebase';
import { useDispatch } from 'react-redux';
import {
  FETCH_ERROR,
  FETCH_START,
  FETCH_SUCCESS,
  GET_PROFILE_DETAIL,
} from 'shared/constants/ActionTypes';
import jwtAxios from '../jwt-auth';
import { AutHSSO, requestOptionsGET } from 'shared/constants/AppEnums';
import { API } from 'shared/constants/AppEnums';

import { Subject } from 'rxjs';
import { useNavigate } from 'react-router-dom';

const subject = new Subject();

export const messageService = {
  sendMessage: (message) => subject.next({ text: message }),
  clearMessages: () => subject.next(),
  getMessage: () => subject.asObservable(),
};

const FirebaseContext = createContext();
const FirebaseActionsContext = createContext();

export const useFirebase = () => useContext(FirebaseContext);

export const useFirebaseActions = () => useContext(FirebaseActionsContext);

const FirebaseAuthProvider = ({ children }) => {
  const [firebaseData, setFirebaseData] = useState({
    user: undefined,
    isLoading: true,
    isAuthenticated: false,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const [isLogout, setIsLogout] = useState(false);

  useEffect(() => {
    const getAuthUser = onAuthStateChanged(
      auth,
      (user) => {
        setFirebaseData({
          user: user,
          // isAuthenticated: Boolean(user), original session firebase
          isAuthenticated: false, // always check sso portal
          isLoading: false,
        });
      },
      () => {
        setFirebaseData({
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
        });
      },
      (completed) => {
        setFirebaseData({
          user: undefined,
          isLoading: false,
          isAuthenticated: completed,
        });
      },
    );

    return () => {
      getAuthUser();
    };
  }, []);

  const getProvider = (providerName) => {
    switch (providerName) {
      case 'google': {
        return googleAuthProvider;
      }
      case 'facebook': {
        return facebookAuthProvider;
      }
      case 'twitter': {
        return twitterAuthProvider;
      }
      case 'github': {
        return githubAuthProvider;
      }
      default:
        return googleAuthProvider;
    }
  };

  const logInWithPopup = async (providerName) => {
    dispatch({ type: FETCH_START });
    try {
      const { user } = await signInWithPopup(auth, getProvider(providerName));
      setFirebaseData({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
      dispatch({ type: FETCH_SUCCESS });
    } catch (error) {
      setFirebaseData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch({ type: FETCH_ERROR, payload: error.message });
    }
  };

  const logInWithEmailAndPassword = async ({ email, password }) => {
    dispatch({ type: FETCH_START });
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      setFirebaseData({ user, isAuthenticated: true, isLoading: false });
      dispatch({ type: FETCH_SUCCESS });
    } catch (error) {
      setFirebaseData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch({ type: FETCH_ERROR, payload: error.message });
    }
  };

  const profile = async (token) => {
    try {
      const response = await jwtAxios
        .get(API.BASE_URL + 'bridge/profile/me/', requestOptionsGET(token))
      return response;
    } catch (error) {
      return error
    }
  };

  const handlingError = (errCode) => {
    switch (errCode) {
      case 403:
        logoutPortal()
        break;
      case 401:
        window.location.replace(window.location.origin + "/error-pages/error-401")
        break;
      case 500:
        window.location.replace(window.location.origin + "/error-pages/error-500")
        break;
      case 503:
        window.location.replace(window.location.origin + "/error-pages/error-503")
        break;
      default:
        window.location.replace(window.location.origin + "/error-pages/maintenance")
        break;
    }
  }

  const profileMpRole = async (token) => {
    const response = await jwtAxios.get(
      API.PROFILE_MP_LOGIN,
      requestOptionsGET(token),
    );
    return response;
  };

  const checkAccessPH = (user) => {
    fetch(API.PH_CHECK_ACCESS, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `${sessionStorage.getItem('token')}`
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.json(); // or response.text() depending on the expected data
    })
      .then(data => {
        const isProd = process.env.REACT_APP_IS_PROD || '0';
        const result = data.result

        if(isProd === '0'){
          result['is_permitted'] = true
        }
        user = { ...user, ...result };
        console.debug("Success check accesss:", user);
        const roles_coofis = user.roles_coofis;
        let roles_access = [];
        if (roles_coofis) {
          roles_access = roles_coofis.map((item) => {
            return item.state;
          });
        }

        const concateUser = { ...user, roles_access: roles_access };
        setFirebaseData({
          user: concateUser,
          isAuthenticated: true,
          isLoading: false,
        });
        dispatch({ type: FETCH_SUCCESS });
        dispatch({ type: GET_PROFILE_DETAIL, payload: concateUser });
      })
      .catch(error => {
        console.error("Error:", error);
      });
  };

  const loginWithPortalToken = async () => {
    dispatch({ type: FETCH_START });
    profile(sessionStorage.getItem('token'))
      .then((data) => {
        if (data?.status === 200) {
          let user = data.data.results;
          const attributes = user.attributes;
          let sidebar_settings;
          let reset_settings;
          let isFool = user.is_vuln;
          checkAccessPH(user);
          if (isFool) {
            navigate('/profile')
          }
          if (attributes?.length > 0) {
            sidebar_settings = attributes
              .filter((item) => item.key === 'sidebar_settings')
              .map((item) => item.value);
            reset_settings = attributes
              .filter((item) => item.key === 'reset_password_next')
              .map((item) => item.value);
            if (reset_settings.length > 0) {
              if (reset_settings[0] === '1')
                window.location.replace(window.location.origin + '/reset-password');
              else {
                messageService.sendMessage(sidebar_settings);
                if (sidebar_settings) {
                  sessionStorage.setItem('sidebar_settings', sidebar_settings);
                }
              }
            } else {
              messageService.sendMessage(sidebar_settings);
              if (sidebar_settings) {
                sessionStorage.setItem('sidebar_settings', sidebar_settings);
              }
            }
          }


        } else if (data?.response.status === 403) {
          handlingError(data.response.status)
        }
      })
      .catch(error => {
        setFirebaseData({
          ...firebaseData,
          isAuthenticated: true,
          isLoading: false,
        });
        handlingError(403)
        dispatch({ type: FETCH_ERROR, payload: error.message });
      })
  };

  const registerUserWithEmailAndPassword = async ({ name, email, password }) => {
    dispatch({ type: FETCH_START });
    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      await sendEmailVerification(auth.currentUser, {
        url: window.location.href,
        handleCodeInApp: true,
      });
      await updateProfile(auth.currentUser, {
        displayName: name,
      });
      setFirebaseData({
        user: { ...user, displayName: name },
        isAuthenticated: true,
        isLoading: false,
      });
      dispatch({ type: FETCH_SUCCESS });
    } catch (error) {
      setFirebaseData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch({ type: FETCH_ERROR, payload: error.message });
    }
  };

  const logoutPortal = async () => {
    setFirebaseData({ ...firebaseData, isLoading: true });
    try {
      // alert("logout portal");

      // window.location.href = 'https://auth.kubekkp.coofis.com/session/logout/';
      const token = sessionStorage.getItem('token');

      window.location.href = API.LOG_OUT_URL + token;

      sessionStorage.removeItem('urlRedirect');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('token_login');
      setIsLogout(true);

      // setFirebaseData({
      //   user: null,
      //   isLoading: false,
      //   isAuthenticated: false,
      // });
    } catch (error) {
      setFirebaseData({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  const logout = async () => {
    setFirebaseData({ ...firebaseData, isLoading: true });
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('token_login');
      sessionStorage.removeItem('urlRedirect');
      await signOut(auth);
      setFirebaseData({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    } catch (error) {
      setFirebaseData({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  const setUser = async (user) => {
    setFirebaseData({
      ...firebaseData,
      user: user
    })
  }

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const token = urlParams.get('token');

  if (window.location.pathname !== '/') {
    if (window.location.pathname !== '/redirect') {
      if (isLogout == false) {
        sessionStorage.setItem('urlRedirect', window.location.pathname);
      }
    }
  }
  if (token) {
    let url = sessionStorage.getItem('urlRedirect');
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('token_login', token);

    if (url === null || url === undefined) {
      window.location.replace('/kkp');
    } else {
      window.location.replace(url);
    }
  }
  return (
    <FirebaseContext.Provider
      value={{
        ...firebaseData,
      }}
    >
      <FirebaseActionsContext.Provider
        value={{
          loginWithPortalToken,
          logInWithEmailAndPassword,
          registerUserWithEmailAndPassword,
          logInWithPopup,
          logout,
          logoutPortal,
          setUser
        }}
      >
        {children}
      </FirebaseActionsContext.Provider>
    </FirebaseContext.Provider>
  );
};
export default FirebaseAuthProvider;

FirebaseAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
